import type { TaskRun } from "../../../../shared/types";
import type { WorkflowContext, WorkflowStepModule } from "../types";

export type VoiceReviewSnapshot = {
  summary: string;
  fromState: string | null;
  toState: string | null;
  documentKey: string | null;
};

function metadataRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nestedStringField(source: Record<string, unknown> | null, key: string): string | null {
  const direct = source && typeof source[key] === "string" ? source[key].trim() : "";
  if (direct) return direct;
  const metadata = metadataRecord(source?.metadata);
  const nested = metadata && typeof metadata[key] === "string" ? metadata[key].trim() : "";
  return nested || null;
}

export function extractVoiceReviewSnapshot(
  run: TaskRun | null | undefined,
): VoiceReviewSnapshot | null {
  const output = metadataRecord(run?.output);
  if (!output) return null;

  return {
    // Voice review keeps the human-readable summary at the top level of the
    // task output, while state-transition fields are nested under metadata.
    summary: nestedStringField(output, "summary") ?? "Voice review recorded.",
    fromState: nestedStringField(output, "from_state"),
    toState: nestedStringField(output, "to_state"),
    documentKey: nestedStringField(output, "document_key"),
  };
}

export const voiceReviewStep: WorkflowStepModule<VoiceReviewSnapshot> = {
  id: "voice_review",
  label: "Voice review",
  runButtonLabel: "Run voice review",
  runningLabel: "Reviewing voice…",
  renderer: "voice-review",
  readArtifacts: ["draft"],
  taskSlug: (context: WorkflowContext) => `voice_review:${context.brief.id}`,
  isEligible: (context: WorkflowContext) =>
    context.brief.state === "voice_review"
      ? { eligible: true }
      : { eligible: false, reason: "Available after a draft enters voice review." },
  buildParams: (context: WorkflowContext) => ({
    brief_id: context.brief.id,
    campaign_id: context.campaignId,
  }),
  extractSnapshot: extractVoiceReviewSnapshot,
};
